import { Component, Event, EventEmitter, JSX, Prop, Watch, h } from '@stencil/core'

import { CheckboxEventDetail, CheckboxOption } from '../air-checkbox/air-checkbox.types'
import { NoneSelected } from '../air-question/air-question.types'
import { Validation } from '../../types/status.types'

@Component({
  tag: 'air-checkbox-group',
  styleUrl: 'air-checkbox-group.scss',
  shadow: true,
})
export class AirCheckboxGroup {
  /** Defines the checkbox options */
  @Prop() options?: CheckboxOption[] | undefined
  @Watch('options')
  watchOptionsChange(newOptions: CheckboxOption[]): void {
    this.initOptions(newOptions)
  }

  /** Specifies validation of checkbox */
  @Prop() validation?: Validation

  private initialOptions: CheckboxOption[] | undefined
  private checkboxGroupEventDetail: CheckboxEventDetail[] | undefined

  /** Emits when checkbox value was changed and blur */
  @Event({ eventName: 'air-change' }) change!: EventEmitter<CheckboxEventDetail[]>

  /** Emits when checkbox was focused */
  @Event({ eventName: 'air-focus' }) focus!: EventEmitter<CheckboxEventDetail[]>

  componentDidLoad(): void {
    if (this.options) this.initOptions(this.options)
  }

  private initOptions(options: CheckboxOption[]): void {
    this.initialOptions = options
  }

  private renderValidation(): JSX.Element | null {
    return this.validation?.text !== undefined ? (
      <span class="status-text">{this.validation.text}</span>
    ) : null
  }

  private handleChange = (event: CustomEvent<CheckboxEventDetail>): void => {
    event.stopPropagation()
    const noneClicked = event.detail.name === NoneSelected

    if (noneClicked) {
      this.initialOptions?.forEach((option) => {
        // Uncheck all other options
        option.checked = option.name === NoneSelected && event.detail.checked ? true : false
      })
    } else {
      this.initialOptions?.forEach((option) => {
        // Check target option from event
        if (option.name === event.detail.name) {
          option.checked = event.detail.checked
        }
        // Uncheck None of the above option
        if (option.name === NoneSelected) {
          option.checked = false
        }
      })
    }

    this.checkboxGroupEventDetail = this.initialOptions?.map<CheckboxEventDetail>((option) => {
      return {
        name: option.name,
        optionKey: option.optionKey,
        checked: !!option.checked,
      }
    })
    this.change?.emit(this.checkboxGroupEventDetail)
  }

  private handleFocus = (event: CustomEvent<CheckboxEventDetail>): void => {
    event.stopPropagation()
    this.checkboxGroupEventDetail = this.initialOptions?.map<CheckboxEventDetail>((option) => {
      return {
        name: option.name,
        optionKey: option.optionKey,
        checked: !!option.checked,
      }
    })
    this.focus.emit(this.checkboxGroupEventDetail)
  }

  render(): JSX.Element {
    const textareaWrapperClasses = {
      'checkbox-group': true,
      [`with-status`]: this.validation?.status !== undefined,
      [`${this.validation?.status}`]: this.validation?.status !== undefined,
    }
    return (
      <div class={textareaWrapperClasses}>
        {this.options?.map((option: CheckboxOption, index: number) => {
          const checkboxAttributes = {
            ...option,
            validation: this.validation,
            'data-key': index,
            'onAir-change': this.handleChange,
            'onAir-focus': this.handleFocus,
          }
          return <air-checkbox {...checkboxAttributes}></air-checkbox>
        })}
        {!this.options && <slot />}
        {this.renderValidation()}
      </div>
    )
  }
}
